import { useMemo } from "react";

function rng(seed) {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const W = 760;
const H = 440;
const PAD = 26;
const BLUE = "#4E86F7";
const BLUE_BRIGHT = "#8FB6FF";

const ChartVisual = ({ seed = 11, layers = {}, stage = null, className = "" }) => {
  const storyMode = stage !== null;

  const { candles, min, max } = useMemo(() => {
    const rand = rng(seed);
    let price = 4470;
    const arr = [];
    for (let i = 0; i < 46; i++) {
      const drift = storyMode
        ? i < 16 ? -2.9 : i < 27 ? 0.15 : 3.6
        : i > 30 ? -2.6 : i > 14 ? 3.8 : 0.4;
      const open = price;
      const close = open + (rand() - 0.44) * 13 + drift;
      const high = Math.max(open, close) + rand() * 6;
      const low = Math.min(open, close) - rand() * 6;
      arr.push({ open, close, high, low });
      price = close;
    }
    let mn = Infinity;
    let mx = -Infinity;
    arr.forEach((c) => {
      mn = Math.min(mn, c.low);
      mx = Math.max(mx, c.high);
    });
    return { candles: arr, min: mn, max: mx };
  }, [seed, storyMode]);

  const y = (v) => PAD + (1 - (v - min) / (max - min)) * (H - PAD * 2);
  const step = (W - PAD * 2) / candles.length;
  const bw = Math.max(3, step * 0.52);
  const range = max - min;
  const x = (i) => PAD + i * step + bw / 2;

  const sellTop = max;
  const sellBottom = max - range * 0.1;
  const buyTop = min + range * 0.1;
  const buyBottom = min;
  const watchTop = min + range * 0.46;
  const watchBottom = min + range * 0.3;

  const ma = (period) =>
    candles.map((_, i) => {
      const s = Math.max(0, i - period + 1);
      const slice = candles.slice(s, i + 1);
      return slice.reduce((a, c) => a + c.close, 0) / slice.length;
    });
  const maFast = ma(5);
  const maSlow = ma(12);
  const linePath = (vals) =>
    vals.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");

  const maxIdx = candles.findIndex((c) => c.high === Math.max(...candles.map((c) => c.high)));
  const minIdx = candles.findIndex((c) => c.low === Math.min(...candles.map((c) => c.low)));
  const ticks = [max, min + range * 0.66, min + range * 0.33, min].map((v) => v.toFixed(2));

  const confirmIdx = storyMode ? 31 : 30;
  const entryPrice = candles[confirmIdx]?.open ?? min;

  const showWatch = storyMode && stage >= 0;
  const showConfluence = storyMode && stage >= 1;
  const showZones = storyMode ? stage >= 2 : layers.zones !== false;
  const showConfirm = storyMode && stage >= 3;

  const fade = (on) => ({ opacity: on ? 1 : 0, transition: "opacity 0.7s ease" });

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={className} role="img" aria-label="Atlas indicator chart visual" data-testid="chart-visual">
      <defs>
        <linearGradient id="buyZone" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.04" />
        </linearGradient>
        <linearGradient id="sellZone" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#EF4444" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#EF4444" stopOpacity="0.03" />
        </linearGradient>
        <linearGradient id="watchZone" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={BLUE} stopOpacity="0.2" />
          <stop offset="100%" stopColor={BLUE} stopOpacity="0.04" />
        </linearGradient>
      </defs>

      {[0.2, 0.4, 0.6, 0.8].map((f) => (
        <line key={f} x1={PAD} x2={W - PAD} y1={H * f} y2={H * f} stroke="#1E293B" strokeWidth="0.6" strokeDasharray="2 6" />
      ))}

      {storyMode && (
        <g style={fade(showWatch)}>
          <rect x={PAD} y={y(watchTop)} width={W - PAD * 2} height={y(watchBottom) - y(watchTop)} fill="url(#watchZone)" />
          <text x={W - PAD - 8} y={y(watchTop) + 16} textAnchor="end" fill={BLUE_BRIGHT} fontSize="11" fontFamily="JetBrains Mono" letterSpacing="3">WATCH ZONE</text>
        </g>
      )}

      <g style={fade(showZones)}>
        <rect x={PAD} y={y(sellTop)} width={W - PAD * 2} height={y(sellBottom) - y(sellTop)} fill="url(#sellZone)" />
        <rect x={PAD} y={y(buyTop)} width={W - PAD * 2} height={y(buyBottom) - y(buyTop)} fill="url(#buyZone)" />
        <text x={W - PAD - 8} y={y(sellBottom) + 16} textAnchor="end" fill="#EF4444" fontSize="11" fontFamily="JetBrains Mono" letterSpacing="3">SELL ZONE</text>
        <text x={W - PAD - 8} y={y(buyTop) - 8} textAnchor="end" fill="#D4AF37" fontSize="11" fontFamily="JetBrains Mono" letterSpacing="3">BUY ZONE</text>
      </g>

      {!storyMode && layers.fvg && (
        <g>
          <rect x={PAD + 16 * step} y={y((max + min) / 2 + range * 0.1)} width={step * 8} height={26} fill="#F5D061" opacity="0.1" stroke="#F5D061" strokeOpacity="0.35" strokeDasharray="4 3" />
          <rect x={PAD + 30 * step} y={y((max + min) / 2 - range * 0.02)} width={step * 7} height={28} fill="#F5D061" opacity="0.08" stroke="#F5D061" strokeOpacity="0.3" strokeDasharray="4 3" />
        </g>
      )}

      {candles.map((c, i) => {
        const up = c.close >= c.open;
        const color = up ? "#D4AF37" : "#475569";
        const cx = PAD + i * step;
        return (
          <g key={i}>
            <line x1={cx + bw / 2} x2={cx + bw / 2} y1={y(c.high)} y2={y(c.low)} stroke={color} strokeWidth="1" opacity="0.85" />
            <rect x={cx} y={y(Math.max(c.open, c.close))} width={bw} height={Math.max(1.5, Math.abs(y(c.open) - y(c.close)))} fill={color} opacity={up ? 0.95 : 0.8} rx="1" />
          </g>
        );
      })}

      {storyMode && (
        <g style={fade(showConfluence)}>
          <ellipse cx={x(minIdx)} cy={y(candles[minIdx].low) - 14} rx={step * 3.2} ry={30} fill={BLUE} opacity="0.08" stroke={BLUE_BRIGHT} strokeOpacity="0.8" strokeDasharray="5 4" strokeWidth="1.2" />
          {[0, 1, 2].map((k) => (
            <path key={k} d={`M${x(minIdx) - 14 + k * 12},${y(candles[minIdx].low) + 26} l4,5 l6,-9`} stroke={BLUE_BRIGHT} strokeWidth="1.6" fill="none" />
          ))}
          <text x={x(minIdx)} y={y(candles[minIdx].low) + 52} textAnchor="middle" fill={BLUE_BRIGHT} fontSize="10" fontFamily="JetBrains Mono" letterSpacing="3">CONFLUENCE</text>
        </g>
      )}

      {storyMode && (
        <g style={fade(showConfirm)} fontFamily="JetBrains Mono" fontSize="9" letterSpacing="2">
          <line x1={x(confirmIdx)} x2={W - PAD} y1={y(entryPrice)} y2={y(entryPrice)} stroke="#D4AF37" strokeWidth="1.2" strokeDasharray="6 4" />
          <text x={W - PAD - 4} y={y(entryPrice) - 6} textAnchor="end" fill="#D4AF37">ENTRY</text>
          <line x1={x(confirmIdx)} x2={W - PAD} y1={y(max - range * 0.04)} y2={y(max - range * 0.04)} stroke="#10B981" strokeWidth="1" strokeDasharray="3 4" />
          <text x={W - PAD - 4} y={y(max - range * 0.04) + 12} textAnchor="end" fill="#10B981">TARGET</text>
          <line x1={x(confirmIdx)} x2={W - PAD} y1={y(min + range * 0.13)} y2={y(min + range * 0.13)} stroke="#EF4444" strokeWidth="1" strokeDasharray="3 4" />
          <text x={W - PAD - 4} y={y(min + range * 0.13) + 12} textAnchor="end" fill="#EF4444">INVALIDATION</text>
          <path d={`M${x(confirmIdx)},${y(entryPrice) + 18} l-5,10 l4,0 l0,8 l2,0 l0,-8 l4,0 z`} fill="#D4AF37" />
        </g>
      )}

      {!storyMode && layers.ribbon && (
        <g fill="none">
          <path d={linePath(maSlow)} stroke="#F8FAFC" strokeWidth="1.4" opacity="0.5" />
          <path d={linePath(maFast)} stroke="#F5D061" strokeWidth="1.8" opacity="0.9" />
        </g>
      )}

      {!storyMode && layers.sweeps && (
        <g fill="#F5D061">
          {[maxIdx, minIdx].map((idx, k) => {
            const c = candles[idx];
            const cy = k === 0 ? y(c.high) - 12 : y(c.low) + 12;
            const cx = x(idx);
            return (
              <g key={k}>
                <rect x={cx - 5} y={cy - 5} width="10" height="10" transform={`rotate(45 ${cx} ${cy})`} stroke="#F5D061" strokeWidth="1.4" fill="#0B0E14" />
                <text x={cx} y={k === 0 ? cy - 14 : cy + 24} textAnchor="middle" fill="#F5D061" fontSize="9" fontFamily="JetBrains Mono" letterSpacing="2">SWEEP</text>
              </g>
            );
          })}
        </g>
      )}

      <g fontFamily="JetBrains Mono" fontSize="10" fill="#64748B">
        {ticks.map((t, i) => (
          <text key={i} x={PAD - 6} y={y(parseFloat(t)) + 3} textAnchor="end">{t}</text>
        ))}
      </g>
    </svg>
  );
};

export default ChartVisual;
